import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { Box, FilterPills, Table as TableComponent, Typography } from '@lucid/lego';
import { useLocation } from 'react-router-dom';
import type { FilterState } from '@lucid/lego';
import type { TestDriveBookingRecord } from '../../../../api/services/lupp/test-drive/dto.test-drive';
import type { BookingFilters } from '../hooks/useBookingSearchTable';
import { useLocaleNavigate } from '../../../../hooks/useLocaleNavigate';
import { getBookingFilterDisplayValue, getBookingFilterLabel } from '../utils';
import { createColumns } from './utils';
import FilterPanel from './FilterPanel';

const SEARCH_DEBOUNCE_MS = 400;

type TableProps = {
  theme: 'light' | 'dark';
  data: TestDriveBookingRecord[];
  totalCount: number;
  currentPage: number;
  pageSize?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  labels: Record<string, string>;
  filters: BookingFilters;
  onFiltersChange: (filters: BookingFilters) => void;
  onClearFilters: () => void;
  searchText?: string;
  onSearchChange?: (value: string) => void;
  bookingError?: boolean;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  locale?: string;
  /** Language-aware locale used for label/translation fallbacks (may differ from `locale`). */
  labelLocale?: string;
};

const Table: React.FC<TableProps> = ({
  theme,
  data,
  totalCount,
  currentPage,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  labels,
  filters,
  onFiltersChange,
  onClearFilters,
  searchText = '',
  onSearchChange,
  bookingError = false,
  sortColumn,
  sortDirection,
  onSort,
  locale = 'en-US',
  labelLocale,
}) => {
  const invertedTheme = theme === 'light' ? 'dark' : 'light';
  const navigate = useLocaleNavigate();
  const { search } = useLocation();
  // Lets the Prospect slider return to this page with the same filters when closed.
  const returnState = useMemo(() => ({ state: { bookingsSearch: search } }), [search]);

  // Deep-links straight to the Test Drive tab and the specific booking's details view in the
  // Prospect slider.
  const handleTestDriveClick = useCallback(
    (prospectIdentifier: string, testDriveScheduleId: string) => {
      navigate(
        `/prospect-desk/prospects/${prospectIdentifier}?testDriveId=${encodeURIComponent(testDriveScheduleId)}#test-drive`,
        returnState
      );
    },
    [navigate, returnState]
  );

  const handleProspectClick = useCallback(
    (prospectIdentifier: string) =>
      navigate(
        `/prospect-desk/prospects/${prospectIdentifier}?from=test-drive-bookings`,
        returnState
      ),
    [navigate, returnState]
  );

  // Local input state so typing stays responsive; the search is only sent after a short pause.
  const [searchInput, setSearchInput] = useState(searchText);
  useEffect(() => setSearchInput(searchText), [searchText]);
  useEffect(() => {
    if (searchInput === searchText) return undefined;
    const timer = setTimeout(() => onSearchChange?.(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, searchText, onSearchChange]);

  const hasActiveFilters = Boolean(searchText.trim()) || Object.values(filters).some(Boolean);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const handleApplyFilters = useCallback(
    (nextFilters: BookingFilters) => {
      onFiltersChange(nextFilters);
      setIsFilterOpen(false);
    },
    [onFiltersChange]
  );

  const appliedFilters = useMemo(
    () =>
      (Object.entries(filters) as [keyof BookingFilters, string | undefined][]).filter(
        (entry): entry is [keyof BookingFilters, string] => Boolean(entry[1])
      ),
    [filters]
  );
  const filterPills = useMemo(
    () => ({
      columns: appliedFilters.map(([key]) => ({ key, title: getBookingFilterLabel(key, labels) })),
      state: Object.fromEntries(
        appliedFilters.map(([key, value]) => [
          key,
          getBookingFilterDisplayValue(key, value, labels, locale),
        ])
      ) as FilterState,
    }),
    [appliedFilters, labels, locale]
  );
  // Pills hold display text, so the remaining keys are mapped back to the real filter values.
  const handleFilterPillsChange = useCallback(
    (remaining: FilterState) =>
      onFiltersChange(
        Object.fromEntries(
          Object.keys(remaining).map((key) => [key, filters[key as keyof BookingFilters]])
        )
      ),
    [filters, onFiltersChange]
  );

  const columns = useMemo(
    () =>
      createColumns({
        theme,
        labels,
        locale: labelLocale ?? locale,
        onTestDriveClick: handleTestDriveClick,
        onProspectClick: handleProspectClick,
      }),
    [theme, labels, locale, labelLocale, handleTestDriveClick, handleProspectClick]
  );

  return (
    <Box>
      {bookingError && (
        <Box mb={16}>
          <Typography variant="body" size="medium" theme={theme} color="error-1">
            {labels?.['error_loading_bookings'] ?? 'Unable to load test-drive bookings'}
          </Typography>
        </Box>
      )}
      <TableComponent
        columns={columns}
        data={data}
        theme={invertedTheme}
        selection={{ enabled: false }}
        pagination={{
          enabled: true,
          total: totalCount,
          current: currentPage,
          pageSize,
          onChange: onPageChange,
          onPageSizeChange,
        }}
        search={{
          placeholder:
            labels?.['table.searchPlaceholder'] ??
            'Search by Test Drive ID, prospect, model, VIN, type, status, host or location',
          value: searchInput,
          onChange: setSearchInput,
        }}
        sort={{
          column: sortColumn,
          direction: sortDirection,
          onSort,
        }}
        columnConfig={{ enabled: true }}
        toolbar={{
          subContent: appliedFilters.length > 0 && (
            <FilterPills
              filterState={filterPills.state}
              columns={filterPills.columns}
              onFilterChange={handleFilterPillsChange}
              theme={invertedTheme}
              dataTestId="booking-filter-pills"
              labels={{ clearAll: labels?.['configurator.table.clearAll'] ?? 'Clear All' }}
            />
          ),
        }}
        filter={{
          enabled: true,
          isOpen: isFilterOpen,
          onOpenChange: setIsFilterOpen,
          content: (
            <FilterPanel
              theme={invertedTheme}
              locale={locale}
              labels={labels}
              filters={filters}
              onApply={handleApplyFilters}
              onClear={onClearFilters}
            />
          ),
        }}
        emptyState={{
          message: hasActiveFilters
            ? (labels?.['table.noResultsFound'] ?? 'No Records Found')
            : (labels?.['table.noBookings'] ?? 'No test-drive bookings found'),
        }}
        labels={{
          manageColumns: labels?.['configurator.table.manageColumns'] ?? 'Manage Columns',
          filter: labels?.['configurator.table.filter'] ?? 'Filter',
          filterWithCount: labels?.['configurator.table.filterWithCount'] ?? 'Filter ({{count}})',
          showingItems:
            labels?.['configurator.table.showingItems'] ??
            'Showing {{start}}-{{end}} of {{total}} Items',
          pageOf: labels?.['configurator.table.pageOf'] ?? '{{current}} of {{total}}',
          selectAll: labels?.['configurator.table.selectAll'] ?? 'Select All',
          clearAll: labels?.['configurator.table.clearAll'] ?? 'Clear All',
          columnsVisible:
            labels?.['configurator.table.columnsVisible'] ??
            '{{visible}} of {{total}} columns visible',
          save: labels?.['configurator.table.save'] ?? 'Save',
          filtersTitle: labels?.['configurator.table.filtersTitle'] ?? 'Filters',
        }}
        gaComponent="test-drive-bookings-table"
        dataTestId="test-drive-bookings-table"
      />
    </Box>
  );
};

export default Table;
